import packageJson from "../../package.json";
import type { AudioFeatureReport } from "./audioFeatures";
import type { AudioLabFailure } from "./audioLabZip";
import {
  DEFAULT_AUDIO_LAB_SAMPLE_RATE,
  getAudioLabInstruments,
  getAudioLabTestCases,
  type AudioLabInstrument
} from "./audioLabRegistry";

export const AUDIO_LAB_EXPORT_VERSION = "1.0.0";
export const AUDIO_LAB_ENGINE_VERSION = packageJson.version ?? "unknown";

export const AI_REVIEW_PROMPT = `# Timbroam Audio Lab AI Review Prompt

Review the attached Timbroam Audio Lab export files against trustworthy reference recordings of the corresponding traditional instruments.

For each instrument, compare pitch, attack, decay, sustain, release, harmonics, noise, vibrato, pitch bend, resonance, spectral centroid, and spectrogram shape.

Identify where the generated tone differs from the real instrument. Recommend Web Audio preset changes or sample replacement directions. Mark any point that cannot be judged confidently as "uncertain".

The feature JSON values are approximate analysis hints, not ground truth. Use the WAV files and spectrogram PNGs as the primary evidence.`;

export function createInstrumentPresetExport(instrument: AudioLabInstrument, exportedAt = new Date().toISOString()) {
  return {
    instrumentId: instrument.id,
    instrumentName: instrument.name,
    preset: instrument.preset,
    notes: instrument.notes,
    articulations: instrument.articulations,
    audioEngine: {
      name: "Timbroam advanced physical/spectral approximation engine",
      renderer: "OfflineAudioContext",
      sampleRate: DEFAULT_AUDIO_LAB_SAMPLE_RATE,
      engineVersion: AUDIO_LAB_ENGINE_VERSION,
      advancedSynthesisEnabled: true,
      approximate: true
    },
    limitations: [
      "Current output is generated without licensed samples by browser-safe physical/spectral approximation models.",
      "The engine does not claim authentic recreation of any traditional instrument.",
      "Feature metrics and validation hints are approximate browser-side analysis values.",
      "Cultural and tuning warnings from the instrument dataset remain authoritative metadata."
    ],
    exportedAt
  };
}

export function createGlobalPresetExport(exportedAt = new Date().toISOString()) {
  return {
    project: "Timbroam",
    exportType: "audio-lab-global-presets",
    version: AUDIO_LAB_EXPORT_VERSION,
    engineVersion: AUDIO_LAB_ENGINE_VERSION,
    exportedAt,
    instruments: getAudioLabInstruments().map((instrument) => createInstrumentPresetExport(instrument, exportedAt))
  };
}

export function createFeatureIndex(
  reports: AudioFeatureReport[],
  failures: AudioLabFailure[] = [],
  exportedAt = new Date().toISOString()
) {
  return {
    project: "Timbroam",
    exportType: "audio-lab-feature-index",
    version: AUDIO_LAB_EXPORT_VERSION,
    engineVersion: AUDIO_LAB_ENGINE_VERSION,
    exportedAt,
    approximate: true,
    featureCount: reports.length,
    failed: failures,
    features: reports
  };
}

export function createAudioLabManifest(failures: AudioLabFailure[] = [], createdAt = new Date().toISOString()) {
  const instruments = getAudioLabInstruments().map((instrument) => ({
    id: instrument.id,
    name: instrument.name,
    noteCount: instrument.notes.length,
    articulationCount: instrument.articulations.length,
    testCount: getAudioLabTestCases(instrument.id).length
  }));

  return {
    project: "Timbroam",
    exportType: "audio-lab-audit",
    version: AUDIO_LAB_EXPORT_VERSION,
    engineVersion: AUDIO_LAB_ENGINE_VERSION,
    createdAt,
    sampleRate: DEFAULT_AUDIO_LAB_SAMPLE_RATE,
    approximate: true,
    instruments,
    totalTestCount: instruments.reduce((sum, instrument) => sum + instrument.testCount, 0),
    failed: failures,
    recommendedAiPrompt:
      "Compare these generated files against reference recordings. Focus on pitch, attack, decay, sustain, release, harmonics, noise, vibrato, pitch bend, resonance, spectral centroid, and spectrogram shape. Mark uncertain judgments explicitly."
  };
}
